import {
  findNodeById,
  isTextNode,
  normalizeClassList,
} from '../../../NewBlogSystem/utilities/htmlEditorUtils.js';
import {
  describeAiSchemaChanges,
  getAiSchemaChangeSummary,
} from './aiSchemaChangeUtils.js';

const stableValue = (value) => {
  if (Array.isArray(value)) return value.map(stableValue);
  if (!value || typeof value !== 'object') return value;
  return Object.keys(value)
    .sort()
    .reduce((result, key) => {
      result[key] = stableValue(value[key]);
      return result;
    }, {});
};

const valuesMatch = (left, right) => (
  JSON.stringify(stableValue(left)) === JSON.stringify(stableValue(right))
);

const directText = (node) => (node?.children || [])
  .filter(isTextNode)
  .map((child) => child.content)
  .join('');

const collectElements = (node, elements = new Map()) => {
  if (!node || isTextNode(node)) return elements;
  elements.set(node.id, node);
  (node.children || []).forEach((child) => collectElements(child, elements));
  return elements;
};

const countDefinitionChanges = (before = {}, after = {}) => {
  const names = new Set([...Object.keys(before || {}), ...Object.keys(after || {})]);
  let changes = 0;
  names.forEach((name) => {
    if (!valuesMatch(before?.[name], after?.[name])) changes += 1;
  });
  return changes;
};

export const getAiChangePreviewSummary = (
  beforeDocument,
  afterDocument,
  { focusedNodeId = null } = {}
) => {
  const beforeRoot = focusedNodeId
    ? findNodeById(beforeDocument?.root, focusedNodeId)
    : beforeDocument?.root;
  const afterRoot = focusedNodeId
    ? findNodeById(afterDocument?.root, focusedNodeId)
    : afterDocument?.root;
  const beforeElements = collectElements(beforeRoot);
  const afterElements = collectElements(afterRoot);
  const schema = getAiSchemaChangeSummary(beforeRoot, afterRoot);
  let textChanges = 0;
  let attributeChanges = 0;
  let classChanges = 0;

  afterElements.forEach((afterNode, nodeId) => {
    const beforeNode = beforeElements.get(nodeId);
    if (!beforeNode) return;
    if (directText(beforeNode) !== directText(afterNode)) textChanges += 1;

    const beforeAttributes = { ...(beforeNode.attributes || {}) };
    const afterAttributes = { ...(afterNode.attributes || {}) };
    delete beforeAttributes.class;
    delete afterAttributes.class;
    if (!valuesMatch(beforeAttributes, afterAttributes)) attributeChanges += 1;
    if (!valuesMatch(
      normalizeClassList(beforeNode.attributes?.class).sort(),
      normalizeClassList(afterNode.attributes?.class).sort()
    )) {
      classChanges += 1;
    }
  });

  const styleChanges = countDefinitionChanges(
    beforeDocument?.classes,
    afterDocument?.classes
  );
  const hasChanges = schema.hasChanges
    || textChanges > 0
    || attributeChanges > 0
    || classChanges > 0
    || styleChanges > 0;
  const descriptions = [];
  if (schema.hasChanges) descriptions.push(describeAiSchemaChanges(schema));
  if (textChanges) {
    descriptions.push(`${textChanges} text area${textChanges === 1 ? '' : 's'} changed`);
  }
  if (attributeChanges) {
    descriptions.push(`${attributeChanges} setting${attributeChanges === 1 ? '' : 's'} changed`);
  }
  if (classChanges) {
    descriptions.push(`${classChanges} appearance assignment${classChanges === 1 ? '' : 's'} changed`);
  }
  if (styleChanges) {
    descriptions.push(`${styleChanges} reusable style${styleChanges === 1 ? '' : 's'} changed`);
  }

  return {
    hasChanges,
    schema,
    textChanges,
    attributeChanges,
    classChanges,
    styleChanges,
    beforeElementCount: beforeElements.size,
    afterElementCount: afterElements.size,
    descriptions,
  };
};
